// src/pages/Reports.jsx
import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useApp } from '../context/AppContext'
import { MetricsGrid, MetricCard, ProgressRow, EmptyState, CatPill } from '../components/UI'
import { CAT_COLOR } from '../utils/constants'
import { fmt, fmtDate, today } from '../utils/helpers'
import * as XLSX from 'xlsx'

const FULL_REPORT_TABS = ['Income', 'Expenses', 'P&L']

export default function Reports() {
  const { t } = useTranslation()
  const { accounts, transactions, acctName, totalIncome, totalExpense, netSavings, totalAssets, totalLiab, userHasFullReports, currentPlan } = useApp()
  const [tab, setTab] = useState('Summary')

  const TABS = [
    { key: 'Summary',  label: t('reports.summary')  },
    { key: 'Income',   label: t('reports.income')   },
    { key: 'Expenses', label: t('reports.expenses') },
    { key: 'P&L',      label: t('reports.pl')       },
  ]

  const income   = useMemo(() => transactions.filter(tx => tx.type === 'income'),  [transactions])
  const expenses = useMemo(() => transactions.filter(tx => tx.type === 'expense'), [transactions])
  const savingsRate = totalIncome > 0 ? ((netSavings / totalIncome) * 100).toFixed(1) : 0

  const byCat = (arr) => {
    const map = {}
    arr.forEach(tx => { map[tx.cat] = (map[tx.cat] || 0) + tx.amt })
    return Object.entries(map).sort((a, b) => b[1] - a[1])
  }

  const isLocked = (key) => FULL_REPORT_TABS.includes(key) && !userHasFullReports

  const exportExcel = () => {
    const wb = XLSX.utils.book_new()
    const txRows = [['Date','Ref','Type','Category','Account / Route','Amount (₹)','Note']]
    ;[...transactions].sort((a,b)=>a.date.localeCompare(b.date)).forEach(tx => {
      txRows.push([tx.date, tx.ref, tx.type, tx.cat, tx.type==='transfer'?`${acctName(tx.from)} → ${acctName(tx.to)}`:acctName(tx.acct), tx.amt, tx.note||''])
    })
    const ws1 = XLSX.utils.aoa_to_sheet(txRows)
    ws1['!cols'] = [{wch:12},{wch:10},{wch:10},{wch:22},{wch:32},{wch:14},{wch:36}]
    XLSX.utils.book_append_sheet(wb, ws1, 'Transactions')
    const aRows = [['Account','Type','Balance (₹)']]
    accounts.forEach(a => aRows.push([a.name, a.type, a.balance]))
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aRows), 'Accounts')
    const inB={}, exB={}
    income.forEach(tx   => { inB[tx.cat] = (inB[tx.cat] || 0) + tx.amt })
    expenses.forEach(tx => { exB[tx.cat] = (exB[tx.cat] || 0) + tx.amt })
    const plRows = [
      ['TecFin — P&L Statement'], [`Exported: ${today()}`], [''],
      ['INCOME',''], ...Object.entries(inB).map(([k,v])=>['  '+k,v]),
      ['Total Income', totalIncome], [''],
      ['EXPENSES',''], ...Object.entries(exB).map(([k,v])=>['  '+k,v]),
      ['Total Expenses', totalExpense], [''],
      [netSavings >= 0 ? 'NET SAVINGS' : 'NET DEFICIT', Math.abs(netSavings)],
    ]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(plRows), 'P&L')
    XLSX.writeFile(wb, `TecFin_Export_${today()}.xlsx`)
  }

  return (
    <>
      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
        {TABS.map(({ key, label }) => {
          const locked = isLocked(key)
          const active = tab === key
          return (
            <button key={key} onClick={() => setTab(locked ? '__locked__' + key : key)}
              style={{ padding: '9px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer', border: 'none', background: 'none', color: active ? 'var(--brand-800)' : locked ? 'var(--gray-400)' : 'var(--gray-600)', borderBottom: `2px solid ${active ? 'var(--brand-400)' : 'transparent'}`, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5 }}
            >
              {label}
              {locked && <i className="ti ti-lock" style={{ fontSize: 12 }} aria-hidden="true" />}
            </button>
          )
        })}
      </div>

      {/* Locked prompt */}
      {tab.startsWith('__locked__') && (
        <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
          <i className="ti ti-lock" style={{ fontSize: 40, color: 'var(--gray-400)', marginBottom: 14, display: 'block' }} aria-hidden="true" />
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
            {t('reports.locked_title', { tab: tab.replace('__locked__', '') })}
          </div>
          <p style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 18, maxWidth: 360, margin: '0 auto 18px' }}>
            {t('reports.locked_sub', { plan: currentPlan.name })}
          </p>
          <Link to="/pricing" className="btn btn-primary" style={{ display: 'inline-flex' }}>
            <i className="ti ti-rocket" aria-hidden="true" /> {t('reports.view_plans')}
          </Link>
        </div>
      )}

      {/* SUMMARY */}
      {tab === 'Summary' && (<>
        <MetricsGrid>
          <MetricCard label={t('reports.total_income')}  value={`₹${fmt(totalIncome)}`}  color="var(--green-600)" />
          <MetricCard label={t('reports.total_expenses')} value={`₹${fmt(totalExpense)}`} color="var(--red-600)" />
          <MetricCard label={t('reports.net_savings')}   value={`${netSavings>=0?'+':'−'}₹${fmt(Math.abs(netSavings))}`} color={netSavings >= 0 ? 'var(--brand-600)' : 'var(--red-600)'} />
          <MetricCard label={t('reports.savings_rate')}  value={`${savingsRate}%`} color={savingsRate >= 20 ? 'var(--green-600)' : 'var(--amber-600)'} />
        </MetricsGrid>
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-title">{t('reports.net_worth')}</div>
          {[
            { label: t('reports.total_assets'),      value: `₹${fmt(totalAssets)}`,         color: 'var(--brand-600)' },
            { label: t('reports.total_liabilities'), value: `₹${fmt(totalLiab)}`,            color: 'var(--red-600)' },
            { label: t('reports.net_worth'),         value: `₹${fmt(totalAssets-totalLiab)}`, color: totalAssets-totalLiab >= 0 ? 'var(--green-600)' : 'var(--red-600)', bold: true },
          ].map(r => (
            <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span style={{ color: 'var(--gray-600)', fontWeight: r.bold ? 600 : 400 }}>{r.label}</span>
              <span style={{ color: r.color, fontWeight: 600 }}>{r.value}</span>
            </div>
          ))}
        </div>
        <div className="card">
          <div className="card-title">{t('reports.account_balances')}</div>
          {accounts.map(a => {
            const pct = Math.min(Math.abs(a.balance) / (totalAssets + totalLiab || 1) * 100, 100)
            return (
              <div key={a.id} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: a.color, display: 'inline-block' }} />{a.name}
                  </span>
                  <span style={{ fontWeight: 600, color: a.balance >= 0 ? 'var(--brand-600)' : 'var(--red-600)' }}>₹{fmt(Math.abs(a.balance))}</span>
                </div>
                <div className="progress-bar"><div className="progress-fill" style={{ width: `${pct.toFixed(1)}%`, background: a.color }} /></div>
              </div>
            )
          })}
        </div>
      </>)}

      {/* INCOME */}
      {tab === 'Income' && (<>
        <MetricsGrid>
          <MetricCard label={t('reports.total_income')} value={`₹${fmt(totalIncome)}`}    color="var(--green-600)" />
          <MetricCard label="Transactions"              value={String(income.length)}       color="var(--gray-800)" />
          <MetricCard label={t('reports.avg_per_txn')} value={`₹${fmt(income.length ? totalIncome/income.length : 0)}`} color="var(--green-600)" />
        </MetricsGrid>
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-title">{t('reports.income_by_category')}</div>
          {byCat(income).map(([cat, amt]) => (
            <ProgressRow key={cat} label={cat} amount={fmt(amt)} pct={amt/totalIncome*100} color={CAT_COLOR[cat]||'var(--green-600)'} />
          ))}
          {income.length === 0 && <EmptyState icon="ti-trending-up" message="No income transactions" />}
        </div>
        <div className="table-wrap">
          <div className="table-toolbar"><span className="table-toolbar-title">{t('reports.income_transactions')} ({income.length})</span></div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead><tr><th style={{width:90}}>{t('transactions.date')}</th><th style={{width:72}}>{t('transactions.ref')}</th><th style={{width:130}}>{t('transactions.category')}</th><th>{t('transactions.note')}</th><th style={{width:105,textAlign:'right'}}>{t('transactions.amount')}</th></tr></thead>
              <tbody>
                {income.sort((a,b)=>b.date.localeCompare(a.date)).map(tx => (
                  <tr key={tx.id}>
                    <td className="mono">{fmtDate(tx.date)}</td>
                    <td className="mono">{tx.ref}</td>
                    <td><CatPill cat={tx.cat} /></td>
                    <td style={{ color: 'var(--gray-600)' }}>{tx.note}</td>
                    <td style={{ textAlign: 'right' }} className="c-income">+₹{fmt(tx.amt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>)}

      {/* EXPENSES */}
      {tab === 'Expenses' && (<>
        <MetricsGrid>
          <MetricCard label={t('reports.total_expenses')} value={`₹${fmt(totalExpense)}`}   color="var(--red-600)" />
          <MetricCard label="Transactions"                value={String(expenses.length)}     color="var(--gray-800)" />
          <MetricCard label={t('reports.avg_per_txn')}   value={`₹${fmt(expenses.length ? totalExpense/expenses.length : 0)}`} color="var(--red-600)" />
          <MetricCard label={t('reports.categories')}    value={String(new Set(expenses.map(tx => tx.cat)).size)} color="var(--gray-800)" />
        </MetricsGrid>
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-title">{t('reports.expense_breakdown')}</div>
          {byCat(expenses).map(([cat, amt]) => (
            <ProgressRow key={cat} label={cat} amount={fmt(amt)} pct={amt/totalExpense*100} color={CAT_COLOR[cat]||'var(--red-600)'} />
          ))}
          {expenses.length === 0 && <EmptyState icon="ti-trending-down" message="No expense transactions" />}
        </div>
        <div className="table-wrap">
          <div className="table-toolbar"><span className="table-toolbar-title">{t('reports.expense_transactions')} ({expenses.length})</span></div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead><tr><th style={{width:90}}>{t('transactions.date')}</th><th style={{width:72}}>{t('transactions.ref')}</th><th style={{width:130}}>{t('transactions.category')}</th><th>{t('transactions.note')}</th><th style={{width:105,textAlign:'right'}}>{t('transactions.amount')}</th></tr></thead>
              <tbody>
                {expenses.sort((a,b)=>b.date.localeCompare(a.date)).map(tx => (
                  <tr key={tx.id}>
                    <td className="mono">{fmtDate(tx.date)}</td>
                    <td className="mono">{tx.ref}</td>
                    <td><CatPill cat={tx.cat} /></td>
                    <td style={{ color: 'var(--gray-600)' }}>{tx.note}</td>
                    <td style={{ textAlign: 'right' }} className="c-expense">−₹{fmt(tx.amt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>)}

      {/* P&L */}
      {tab === 'P&L' && (<>
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 14 }}>
          <div style={{ padding: '10px 15px', background: 'var(--green-50)', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--green-600)', textTransform: 'uppercase', letterSpacing: .5 }}>{t('reports.income')}</span>
          </div>
          {byCat(income).map(([cat, amt]) => (
            <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 15px', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span style={{ color: 'var(--gray-600)' }}>{t(`categories.${cat}`, cat)}</span>
              <span className="c-income">₹{fmt(amt)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 15px', fontSize: 13, fontWeight: 700, borderBottom: '1px solid var(--border-strong)' }}>
            <span>{t('reports.total_income')}</span><span className="c-income">₹{fmt(totalIncome)}</span>
          </div>
          <div style={{ padding: '10px 15px', background: 'var(--red-50)', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--red-600)', textTransform: 'uppercase', letterSpacing: .5 }}>{t('reports.expenses')}</span>
          </div>
          {byCat(expenses).map(([cat, amt]) => (
            <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 15px', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span style={{ color: 'var(--gray-600)' }}>{t(`categories.${cat}`, cat)}</span>
              <span className="c-expense">₹{fmt(amt)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 15px', fontSize: 13, fontWeight: 700, borderBottom: '1px solid var(--border-strong)' }}>
            <span>{t('reports.total_expenses')}</span><span className="c-expense">₹{fmt(totalExpense)}</span>
          </div>
          <div style={{ padding: '11px 15px', background: netSavings >= 0 ? 'var(--brand-50)' : 'var(--red-50)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700 }}>
              <span style={{ color: netSavings >= 0 ? 'var(--brand-600)' : 'var(--red-600)' }}>
                {netSavings >= 0 ? t('reports.net_savings') : t('reports.net_deficit')}
              </span>
              <span style={{ color: netSavings >= 0 ? 'var(--brand-600)' : 'var(--red-600)' }}>
                {netSavings >= 0 ? '+' : '−'}₹{fmt(Math.abs(netSavings))}
              </span>
            </div>
          </div>
        </div>
        <button className="btn btn-success" onClick={exportExcel} style={{ marginBottom: 8 }}>
          <i className="ti ti-file-spreadsheet" aria-hidden="true" /> {t('reports.export_excel')}
        </button>
      </>)}
    </>
  )
}
